import type {
  FunnelAssignRequest,
  FunnelAssignResponse,
  FunnelEvaluateStepRequest,
  FunnelEvaluateStepResponse,
  AcceptStepRequest,
  DeclineStepRequest,
  StepNavigationResponse,
  TemplateContentResponse,
} from './types';
import { funnelLogger } from './logger';

export interface FunnelClientConfig {
  apiBaseUrl: string;
  tenantId: string;
  checkoutMiddlewareUrl?: string;
}

/** Typed error that preserves the HTTP status code for callers to branch on. */
export class FunnelApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'FunnelApiError';
  }
}

async function funnelFetch<T>(
  baseUrl: string,
  tenantId: string,
  path: string,
  body: Record<string, unknown>,
  headers?: Record<string, string>,
  signal?: AbortSignal,
): Promise<T> {
  const url = `${baseUrl.replace(/\/$/, '')}${path}?tenantId=${encodeURIComponent(tenantId)}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(headers ?? {}) },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    const text = await response.text();
    throw new FunnelApiError(response.status, `Funnel API error ${response.status}: ${text}`);
  }
  return response.json() as Promise<T>;
}

async function checkoutMiddlewareFetch<T>(
  baseUrl: string | undefined,
  path: string,
  body?: Record<string, unknown>,
): Promise<T> {
  if (!baseUrl) {
    throw new Error('checkoutMiddlewareUrl is required for post-purchase upsell routing');
  }

  const url = `${baseUrl.replace(/\/$/, '')}${path}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new FunnelApiError(response.status, `Checkout middleware error ${response.status}: ${text}`);
  }

  return response.json() as Promise<T>;
}

export interface FunnelClient {
  assignFunnel(req: FunnelAssignRequest, signal?: AbortSignal): Promise<FunnelAssignResponse>;
  evaluateStep(req: FunnelEvaluateStepRequest): Promise<FunnelEvaluateStepResponse>;
  acceptStep(req: AcceptStepRequest): Promise<StepNavigationResponse>;
  declineStep(req: DeclineStepRequest): Promise<StepNavigationResponse>;
  acceptUpsell(req: { funnelId: string; stepId: string; orderId: string; productId?: string; variantId?: string }): Promise<StepNavigationResponse>;
  declineUpsell(req: { funnelId: string; stepId: string }): Promise<StepNavigationResponse>;
  fetchTemplate(templateId: string): Promise<TemplateContentResponse>;
}

export function createFunnelClient(config: FunnelClientConfig): FunnelClient {
  const { apiBaseUrl, tenantId, checkoutMiddlewareUrl } = config;
  const base = apiBaseUrl.replace(/\/$/, '');

  // TF-AUD-012: Template cache with TTL to honor "always live" guarantee.
  const TEMPLATE_CACHE_TTL_MS = 60_000; // 60 seconds
  const templateCache = new Map<string, { data: TemplateContentResponse; timestamp: number }>();
  const inflightRequests = new Map<string, Promise<TemplateContentResponse>>();

  return {
    assignFunnel: (req, signal) =>
      funnelFetch<FunnelAssignResponse>(base, tenantId, '/v1/public/funnels/assign', {
        sessionId: req.sessionId,
        customerCurrency: req.customerCurrency,
        customerCountry: req.customerCountry,
        urlPath: req.urlPath,
        pageType: req.pageType,
        pageContext: req.pageContext,
        deviceType: req.deviceType,
        referrerSource: req.referrerSource,
      }, undefined, signal),

    evaluateStep: (req) =>
      funnelFetch<FunnelEvaluateStepResponse>(base, tenantId, '/v1/public/funnels/evaluate-step', {
        sessionId: req.sessionId,
        funnelId: req.funnelId,
        currentStepId: req.currentStepId,
        event: req.event,
        pageContext: req.pageContext,
      }),

    acceptStep: (req) =>
      funnelFetch<StepNavigationResponse>(
        base,
        tenantId,
        `/v1/public/funnels/${req.funnelId}/steps/${req.stepId}/accept`,
        {
          sessionId: req.sessionId,
          action: req.action,
          productId: req.productId,
          variantId: req.variantId,
          quantity: req.quantity ?? 1,
        },
      ),

    declineStep: (req) =>
      funnelFetch<StepNavigationResponse>(
        base,
        tenantId,
        `/v1/public/funnels/${req.funnelId}/steps/${req.stepId}/decline`,
        { sessionId: req.sessionId },
      ),

    acceptUpsell: (req) =>
      checkoutMiddlewareFetch<StepNavigationResponse>(
        checkoutMiddlewareUrl,
        `/funnels/${req.funnelId}/steps/${req.stepId}/accept`,
        {
          order_id: req.orderId,
          product_id: req.productId,
          variant_id: req.variantId,
        },
      ),

    declineUpsell: (req) =>
      checkoutMiddlewareFetch<StepNavigationResponse>(
        checkoutMiddlewareUrl,
        `/funnels/${req.funnelId}/steps/${req.stepId}/decline`,
      ),

    fetchTemplate: (templateId: string): Promise<TemplateContentResponse> => {
      const cached = templateCache.get(templateId);
      if (cached && (Date.now() - cached.timestamp) < TEMPLATE_CACHE_TTL_MS) {
        return Promise.resolve(cached.data);
      }
      // Evict expired entry
      if (cached) templateCache.delete(templateId);

      const inflight = inflightRequests.get(templateId);
      if (inflight) return inflight;

      const url = `${base}/v1/public/templates/${encodeURIComponent(templateId)}?tenantId=${encodeURIComponent(tenantId)}`;
      const promise = fetch(url, { method: 'GET' })
        .then(async (response) => {
          if (!response.ok) {
            const text = await response.text();
            throw new Error(`Template API error ${response.status}: ${text}`);
          }
          return response.json() as Promise<Record<string, unknown>>;
        })
        .then((raw) => {
          // TPL-001: Go API may wrap in { template: {...} } and uses `componentTree` + `definitionId`
          const t = (raw.template ?? raw) as Record<string, unknown>;
          const rawContent = t.componentTree ?? t.pageContent ?? null;
          // componentTree can be an array of blocks or an object with .blocks
          const rawBlocks: Record<string, unknown>[] | null = Array.isArray(rawContent)
            ? rawContent
            : (rawContent && typeof rawContent === 'object' && Array.isArray((rawContent as Record<string, unknown>).blocks))
              ? (rawContent as Record<string, unknown>).blocks as Record<string, unknown>[]
              : null;

          if (!rawBlocks) {
            funnelLogger.warn('Funnel template payload has no renderable blocks', {
              templateId,
              reason: rawContent == null ? 'missing_page_content' : 'invalid_page_content_shape',
              hasComponentTree: t.componentTree != null,
              hasPageContent: t.pageContent != null,
            });
          }

          // Normalize blocks: map `definitionId` → `type` for BlockRenderer compatibility
          const pageContent = rawBlocks
            ? rawBlocks.map((b) => ({
                id: String(b.id ?? ''),
                type: String(b.definitionId ?? b.type ?? 'unknown'),
                props: (b.props ?? {}) as Record<string, unknown>,
                ...(b.layout ? { layout: b.layout } : {}),
              }))
            : null;

          const mapped: TemplateContentResponse = {
            id: String(t.id ?? ''),
            pageContent,
            displaySettings: (t.displaySettings ?? null) as TemplateContentResponse['displaySettings'],
          };
          return mapped;
        })
        .then((data) => {
          templateCache.set(templateId, { data, timestamp: Date.now() });
          return data;
        })
        .finally(() => {
          inflightRequests.delete(templateId);
        });

      inflightRequests.set(templateId, promise);
      return promise;
    },
  };
}
